import { uuidv4 } from "@firebase/util";
import { HighlightOff, SettingsSuggest } from "@mui/icons-material";
import Button from "@mui/material/Button/Button";
import FormControl from "@mui/material/FormControl/FormControl";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton/IconButton";
import InputLabel from "@mui/material/InputLabel/InputLabel";
import MenuItem from "@mui/material/MenuItem/MenuItem";
import Select from "@mui/material/Select/Select";
import TextField from "@mui/material/TextField/TextField";
import Typography from "@mui/material/Typography/Typography";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import Loading from "../../../../../../components/Loading";
import SmartSnackbar, {
	ISmartSnackbarConfig,
} from "../../../../../../components/Snackbars/SmartSnackbar";
import {
	addService,
	updateService,
} from "../../../../../../services/api/models";
import { IFormModalProps } from "../../../../../../types/Modal.types";
import { IOptions } from "../../../../../../types/Options.types";
import {
	IServiceDto,
	IServicePart,
} from "../../../../../../types/Services.types";
import { serviceAttributes } from "../../../../../../utils/data/serviceProperties";
import { IServiceFormInput, emptyServiceForm } from "./ServiceForm.form";

export interface IServiceFormProps extends IFormModalProps {
	service?: IServiceDto;
}

export default function ServiceForm(props: IServiceFormProps) {
	const { control, handleSubmit, reset } = useForm<IServiceFormInput>({
		defaultValues: emptyServiceForm,
	});
	const [loading, setLoading] = useState<boolean>(false);
	const [serviceParts, setServiceParts] = useState<IServicePart[]>([]);
	const [smartSnackbarConfig, setSmartSnackbarConfig] =
		useState<ISmartSnackbarConfig>({
			open: false,
			message: "",
			severity: "success",
		});

	useEffect(() => {
		if (props.service) {
			reset(props.service as IServiceFormInput);

			const orderedParts = props.service.parts.sort(
				(p1: IServicePart, p2: IServicePart) => {
					return (
						new Date(p1.createdDate).getTime() -
						new Date(p2.createdDate).getTime()
					);
				}
			);

			setServiceParts(orderedParts);
		}
	}, [props.service, reset]);

	const addPart = () => {
		const parts = [...serviceParts];

		const newPart: IServicePart = {
			createdBy: "",
			id: uuidv4(),
			description: "",
			createdDate: new Date(),
			amount: 1,
		};

		parts.push(newPart);
		setServiceParts(parts);
	};

	const evaluateParts = (
		id: string,
		prop: "description" | "amount",
		value: string | number
	) => {
		let newParts = [...serviceParts];

		const part = newParts.find((p: IServicePart) => p.id === id);

		if (prop === "description") {
			part!.description = value as string;
		}

		if (prop === "amount") {
			part!.amount = value as number;
		}

		newParts = newParts.filter((p: IServicePart) => p.id !== id);

		newParts.push(part!);

		newParts = newParts.sort((p1, p2) => {
			return (
				new Date(p1.createdDate).getTime() -
				new Date(p2.createdDate).getTime()
			);
		});

		setServiceParts(newParts);
	};

	const removePart = (part: IServicePart) => {
		let newParts = [...serviceParts];

		newParts = newParts.filter((p: IServicePart) => p.id !== part.id);

		newParts = newParts.sort((p1, p2) => {
			return (
				new Date(p1.createdDate).getTime() -
				new Date(p2.createdDate).getTime()
			);
		});

		setServiceParts(newParts);
	};

	const handleClose = () => {
		reset(emptyServiceForm);
		props.handleCloseModal(false);
	};

	const onSubmit: SubmitHandler<IServiceFormInput> = async (
		data: IServiceFormInput
	) => {
		setLoading(true);
		try {
			const payload: IServiceDto = {
				createdBy: "",
				createdDate: props.service?.createdDate ?? new Date(),
				id: props.service?.id ?? uuidv4(),
				description: data.description,
				parts: serviceParts,
				duration: parseFloat(data.duration.toString()),
				system: data.system,
			};

			if (props.service) {
				await updateService(props.id, payload);
			} else {
				await addService(props.id, payload);
			}

			setSmartSnackbarConfig({
				open: !smartSnackbarConfig.open,
				message: "Serviço adicionado com sucesso",
				severity: "success",
			});
			handleClose();
		} catch (error: any) {
			setSmartSnackbarConfig({
				open: !smartSnackbarConfig.open,
				message: error.message as string,
				severity: "error",
			});
			console.error(error);
		} finally {
			setLoading(false);
			props.refreshParentHandler(!props.refreshParent);
		}
	};

	return (
		<div>
			{loading && <Loading />}

			<form id="modelServiceForm" onSubmit={handleSubmit(onSubmit)}>
				<Controller
					name="description"
					control={control}
					render={({ field: { onChange, value } }) => (
						<TextField
							autoFocus
							margin="dense"
							id="description"
							label="Descrição"
							type="text"
							fullWidth
							onChange={onChange}
							value={value}
						/>
					)}
				/>
				<FormControl fullWidth>
					<InputLabel id="system">Sistema</InputLabel>

					<Controller
						control={control}
						name="system"
						defaultValue={emptyServiceForm.system}
						render={({ field: { onChange, value } }) => (
							<Select
								labelId="system"
								id="select-system"
								fullWidth
								onChange={onChange}
								value={value}
							>
								{serviceAttributes.systemOptions.map(
									(o: IOptions, i: number) => {
										return (
											<MenuItem key={i} value={o.id}>
												{o.value}
											</MenuItem>
										);
									}
								)}
							</Select>
						)}
					/>
				</FormControl>
				<Controller
					name="duration"
					control={control}
					render={({ field: { onChange, value } }) => (
						<TextField
							autoFocus
							margin="dense"
							id="duration"
							label="Duração em horas"
							type="number"
							InputProps={{
								inputProps: {
									min: 0.1,
									step: 0.05,
								},
							}}
							fullWidth
							onChange={onChange}
							value={value === 0 ? "" : value}
						/>
					)}
				/>

				<Grid
					container
					justifyContent="space-between"
					style={{ marginTop: "24px" }}
				>
					<Grid>
						<Typography variant="subtitle1">Peças </Typography>
					</Grid>

					<Grid>
						<Button
							style={{
								backgroundColor: "#ff7221",
								color: "white",
							}}
							variant="contained"
							color="primary"
							endIcon={<SettingsSuggest />}
							onClick={() => addPart()}
							size="small"
						>
							Nova Peça{" "}
						</Button>
					</Grid>
				</Grid>

				{serviceParts.map((p: IServicePart) => {
					return (
						<>
							<Grid
								justifyContent="space-between"
								alignItems="flex-end"
								spacing={2}
								container
							>
								<Grid size={{ xs: 9 }}>
									<TextField
										autoFocus
										margin="dense"
										id={`partsDescription${p.id}`}
										label="Descrição"
										type="text"
										fullWidth
										onChange={(e: any) =>
											evaluateParts(
												p.id,
												"description",
												e.target.value
											)
										}
										value={p.description}
									/>
								</Grid>
								<Grid size={{ xs: 2 }}>
									<TextField
										autoFocus
										margin="dense"
										id={`partsAmount${p.id}`}
										label="Quantidade"
										type="number"
										fullWidth
										value={p.amount}
										InputProps={{
											inputProps: {
												min: 1,
												step: 1,
											},
										}}
										onChange={(e: any) =>
											evaluateParts(
												p.id,
												"amount",
												e.target.value
											)
										}
									/>
								</Grid>
								<Grid alignItems="center" size={{ xs: 1 }}>
									<IconButton
										color="default"
										aria-label="delete"
										size="small"
										onClick={() => removePart(p)}
									>
										<HighlightOff />
									</IconButton>
								</Grid>
							</Grid>
						</>
					);
				})}
			</form>
			{smartSnackbarConfig.open && (
				<SmartSnackbar
					message={smartSnackbarConfig.message}
					severity={smartSnackbarConfig.severity}
					open={smartSnackbarConfig.open}
				/>
			)}
		</div>
	);
}

export { ServiceForm };
